import { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useIdleTimer, type EventsType, type IIdleTimer } from 'react-idle-timer';

//actions
import { showModal } from '@actions/common/actionCreators';

//utils
import { onLogout } from '@utils/localStorage/sessionStorageUtils';

//types
import { commonModalTypes as MODAL_TYPES } from '@constants/common/modals/commonModalTypes';

export interface ISessionTimerConfig {
    timeout: number;
    promptBeforeIdle: number;
    disabled: boolean;
}

// Activity that keeps the session alive. Hover/scroll are intentionally excluded
// so only deliberate interaction (keyboard or click) resets the idle timer.
const ACTIVITY_EVENTS: EventsType[] = ['keydown', 'click', 'keyup'];

// How long the "you have been logged out" message stays visible before redirect.
const LOGGED_OUT_MESSAGE_DURATION_MS = 6000;

// react-idle-timer requires a valid timeout even while disabled.
const DISABLED_TIMEOUT_MS = 1000 * 60 * 20;

const DISABLED_CONFIG: ISessionTimerConfig = {
    timeout: DISABLED_TIMEOUT_MS,
    promptBeforeIdle: 0,
    disabled: true,
};

const useSessionIdle = () => {
    const dispatch = useDispatch();

    // The only piece of React state: drives react-idle-timer's props. The library
    // reacts to timeout/promptBeforeIdle/disabled changes and restarts itself, so
    // no manual reset/reschedule effects are needed.
    const [timerProps, setTimerProps] = useState<ISessionTimerConfig>(DISABLED_CONFIG);

    const logoutUrlRef = useRef<string | null>(null);
    const navigatingRef = useRef<boolean>(false);
    const idleTimerRef = useRef<IIdleTimer | null>(null);

    // Mirror of the latest applied config so initSessionIdle can skip redundant
    // updates when the environment store re-emits an unchanged value.
    const timerPropsRef = useRef<ISessionTimerConfig>(timerProps);
    timerPropsRef.current = timerProps;

    const navigateToLogout = useCallback((): void => {
        if (navigatingRef.current) {
            return;
        }
        navigatingRef.current = true;
        // Always clear session/local storage on expiry, even when no logout URL is
        // configured, so stale session data never lingers on screen or in storage.
        onLogout();
        const logoutUrl = logoutUrlRef.current;
        if (logoutUrl) {
            window.location.replace(logoutUrl);
        } else {
            // No logout URL: reload so the (now expired) server session forces a
            // fresh login redirect rather than leaving an expired session visible.
            window.location.reload();
        }
    }, []);

    const showSessionModal = useCallback(
        (loggedOut: boolean): void => {
            dispatch(
                showModal({
                    modalType: MODAL_TYPES.SESSION_IDLE_WARNING_MODAL,
                    modalData: {
                        logoutUrl: logoutUrlRef.current,
                        loggedOut,
                        // "Stay logged in" -> reset the idle timer.
                        onHandleReset: () => idleTimerRef.current?.activate(),
                        // Wall-clock accurate seconds remaining until logout; resilient to tab throttling.
                        getRemainingTime: () => Math.ceil((idleTimerRef.current?.getRemainingTime() ?? 0) / 1000),
                    },
                }),
            );
        },
        [dispatch],
    );

    const handlePrompt = useCallback((): void => {
        showSessionModal(false);
    }, [showSessionModal]);

    const handleIdle = useCallback((): void => {
        // Authoritative expiry: show the logged-out message, then redirect.
        showSessionModal(true);
        setTimeout(navigateToLogout, LOGGED_OUT_MESSAGE_DURATION_MS);
    }, [showSessionModal, navigateToLogout]);

    const idleTimer = useIdleTimer({
        timeout: timerProps.timeout,
        promptBeforeIdle: timerProps.promptBeforeIdle,
        disabled: timerProps.disabled,
        events: ACTIVITY_EVENTS,
        onPrompt: handlePrompt,
        onIdle: handleIdle,
        debounce: 500,
        crossTab: false,
        name: 'ghix-session-idle',
    });
    // Expose the latest timer instance to the modal callbacks without re-creating them.
    idleTimerRef.current = idleTimer;

    // react-idle-timer v5 relies on its internal Page Visibility handling, but browser
    // tab throttling can delay timer callbacks so that onIdle fires late (or not at all)
    // while the tab is backgrounded. This effect mirrors the visibilitychange guard in
    // the legacy sessioninactivity.js: on refocus, check the wall-clock remaining time
    // and invoke the correct callback immediately if the timer has already expired or
    // entered the prompt window.
    useEffect(() => {
        if (timerProps.disabled) {
            return undefined;
        }

        const handleVisibilityChange = (): void => {
            if (document.hidden || navigatingRef.current) {
                return;
            }
            const timer = idleTimerRef.current;
            if (!timer) {
                return;
            }
            if (timer.getRemainingTime() <= 0) {
                handleIdle();
            } else if (timer.isPrompted?.()) {
                handlePrompt();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [timerProps.disabled, handleIdle, handlePrompt]);

    const initSessionIdle = useCallback(
        (
            anonymous: boolean,
            clientInactivityThreshold: number,
            popupThresholdValue: number,
            logoutUrl: string,
        ): void => {
            logoutUrlRef.current = logoutUrl;

            const disabled = anonymous === true || !clientInactivityThreshold;
            let nextConfig: ISessionTimerConfig;
            if (disabled) {
                nextConfig = DISABLED_CONFIG;
            } else {
                const timeout = clientInactivityThreshold * 1000;
                // promptBeforeIdle must be strictly less than timeout or the library throws.
                const promptSeconds = Math.min(Math.max(popupThresholdValue || 0, 0), clientInactivityThreshold - 1);
                // react-idle-timer v5 skips onPrompt entirely when promptBeforeIdle is 0.
                // Clamp to at least 1 s when a prompt window is requested so the warning
                // modal is always reachable regardless of how popupThresholdValue is configured.
                const promptBeforeIdle = promptSeconds > 0 ? Math.max(promptSeconds * 1000, 1000) : 0;
                nextConfig = { timeout, promptBeforeIdle, disabled: false };
            }

            const prev = timerPropsRef.current;
            const unchanged =
                prev.timeout === nextConfig.timeout &&
                prev.promptBeforeIdle === nextConfig.promptBeforeIdle &&
                prev.disabled === nextConfig.disabled;
            if (unchanged) {
                // Same resolved config (e.g. an unrelated environment-store refresh):
                // don't restart the timer or clear an in-progress logout latch.
                return;
            }

            // A genuine (re)configuration — re-arm logout navigation and apply it.
            navigatingRef.current = false;
            setTimerProps(nextConfig);
        },
        [],
    );

    return {
        initSessionIdle,
        idleTimer,
        // Resolved react-idle-timer config; consumed by the QA debug overlay.
        sessionConfig: timerProps,
    };
};

export default useSessionIdle;
